import React from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';
import WhatsAppButton from '../WhatsAppButton';

const SEVERITY_STYLES = {
  low: 'bg-green-100 text-green-800 border-green-200',
  medium: 'bg-amber-100 text-amber-800 border-amber-200',
  high: 'bg-orange-100 text-orange-800 border-orange-200',
  critical: 'bg-red-100 text-red-800 border-red-200',
};

const parseEventValue = (value) => {
  if (!value) return {};
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch {
      return { content_summary: value };
    }
  }
  return value;
};

const formatTeamKey = (teamKey, t) => {
  const key = (teamKey || '').trim();
  if (!key) return t('escalateMessage.notAvailable');
  const translationKey = `escalateMessage.team.${key}`;
  const translated = t(translationKey);
  return translated !== translationKey ? translated : key;
};

export default function EscalateMessageEventValue({ event }) {
  const { t } = useLanguage();
  const value = parseEventValue(event.value);
  const severity = (value.severity || '').trim().toLowerCase();
  const severityStyle = SEVERITY_STYLES[severity] || 'bg-gray-100 text-gray-800 border-gray-200';
  const teams = (value.teams_notified || '')
    .split(',')
    .map((team) => team.trim())
    .filter(Boolean);
  const recipients = Array.isArray(value.recipients) ? value.recipients : [];

  return (
    <div className="space-y-2 min-w-[320px]">
      <div className="flex flex-wrap items-center gap-2">
        <div className="font-medium text-orange-700">
          🚨 {t('escalateMessage.title')}
        </div>
        {severity && (
          <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${severityStyle}`}>
            {t(`escalateMessage.severity.${severity}`, severity)}
          </span>
        )}
      </div>

      {value.content_summary && (
        <div className="text-sm text-gray-800 bg-orange-50 p-3 rounded-md border border-orange-200 whitespace-pre-wrap">
          {value.content_summary}
        </div>
      )}

      <div className="grid gap-1 text-sm text-gray-600">
        {value.request_id && (
          <div>
            <span className="font-medium">🆔 {t('escalateMessage.requestId')}:</span>{' '}
            <span className="font-mono text-gray-800">{value.request_id}</span>
          </div>
        )}
        {value.received_at && (
          <div>
            <span className="font-medium">🕐 {t('escalateMessage.receivedAt')}:</span> {value.received_at}
          </div>
        )}
        {teams.length > 0 && (
          <div>
            <span className="font-medium">👥 {t('escalateMessage.teamsNotified')}:</span>{' '}
            {teams.map((team) => formatTeamKey(team, t)).join(', ')}
          </div>
        )}
        {value.user_whatsapp && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium">📱 {t('escalateMessage.customerWhatsApp')}:</span>
            <span>{value.user_whatsapp}</span>
            <WhatsAppButton phoneNumber={value.user_whatsapp} size="sm" />
          </div>
        )}
        {value.whatsapp_conversation_link && (
          <div>
            <a
              href={value.whatsapp_conversation_link}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 underline"
            >
              {t('escalateMessage.openConversation')}
            </a>
          </div>
        )}
        {recipients.length > 0 && (
          <div>
            <span className="font-medium">📤 {t('escalateMessage.recipients')}:</span>{' '}
            {recipients.join(', ')}
          </div>
        )}
      </div>
    </div>
  );
}
